import { useHotkey } from '@tanstack/react-hotkeys';
import clsx, { ClassValue } from 'clsx';
import { clamp, isNil } from 'lodash';
import { ReactElement, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@shared/components/Button';
import { View } from '@shared/components/View';
import { Pagination } from './Pagination';
import styles from './Carousel.module.css';

interface Props<T = any> {
  data: T[];
  renderItem(data: T): ReactElement;
  initialActiveIndex?: number;
  className?: ClassValue;
  showNavButtons?: boolean;
  onChangeActiveIndex?(index: number): void;
  // scope for hotkey listeners so they don't bubble up
  hotkeyTarget?: RefObject<HTMLElement | null>;
}

export function Carousel<T>({
  data,
  className,
  renderItem,
  onChangeActiveIndex,
  initialActiveIndex = 0,
  showNavButtons = false,
  hotkeyTarget,
}: Props<T>) {
  const scrollViewRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(initialActiveIndex);

  const onScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const slideWidth = event.currentTarget.clientWidth;
      if (slideWidth <= 0) {
        return;
      }

      const index = Math.round(event.currentTarget.scrollLeft / slideWidth);
      setActiveIndex(index);
      onChangeActiveIndex?.(index);
    },
    [onChangeActiveIndex],
  );

  const scrollToIndex = useCallback((index: number, animated: boolean = true) => {
    const scrollView = scrollViewRef.current;
    if (!scrollView || scrollView.clientWidth <= 0) {
      return;
    }

    scrollView.scrollTo({
      behavior: animated ? 'smooth' : 'auto',
      left: index * scrollView.clientWidth,
    });
  }, []);

  const step = useCallback(
    (delta: number, animated: boolean = true) =>
      scrollToIndex(clamp(activeIndex + delta, 0, data.length - 1), animated),
    [scrollToIndex, activeIndex, data.length],
  );

  const scrollToPrev = useCallback(() => step(-1), [step]);
  const scrollToNext = useCallback(() => step(1), [step]);
  const jumpToPrev = useCallback(() => step(-1, false), [step]);
  const jumpToNext = useCallback(() => step(1, false), [step]);

  useEffect(() => {
    if (!isNil(initialActiveIndex)) {
      scrollToIndex(initialActiveIndex, false);
    }
  }, [initialActiveIndex, scrollToIndex]);

  const options = { target: hotkeyTarget ?? scrollViewRef };
  useHotkey('ArrowLeft', scrollToPrev, options);
  useHotkey('ArrowRight', scrollToNext, options);
  useHotkey('J', scrollToPrev, options);
  useHotkey('K', scrollToNext, options);
  // Shift-arrow keys change slides without animating.
  useHotkey('Shift+J', jumpToPrev, options);
  useHotkey('Shift+K', jumpToNext, options);
  useHotkey('Shift+ArrowLeft', jumpToPrev, options);
  useHotkey('Shift+ArrowRight', jumpToNext, options);

  return (
    <View className={[styles.carousel, className]}>
      <div ref={scrollViewRef} className={styles.scrollView} onScroll={onScroll}>
        <div className={styles.content}>
          {data.map((item, index) => (
            <div key={index} className={styles.item}>
              {renderItem(item)}
            </div>
          ))}
        </div>
      </div>
      {data.length > 1 && (
        <>
          {showNavButtons && (
            <div className={clsx(styles.prevButton, styles.navButton)}>
              <Button
                variant="tertiary"
                size="large"
                iconOnly
                icon="arrow-point-left"
                onClick={scrollToPrev}
                disabled={activeIndex === 0}
              />
            </div>
          )}
          {!showNavButtons && (
            <button
              type="button"
              onClick={scrollToPrev}
              className={clsx(
                styles.prevButton,
                styles.edgeButton,
                activeIndex === 0 && styles.disabled,
              )}
            />
          )}

          {showNavButtons && (
            <div className={clsx(styles.nextButton, styles.navButton)}>
              <Button
                variant="tertiary"
                size="large"
                iconOnly
                icon="arrow-point-right"
                onClick={scrollToNext}
                disabled={activeIndex === data.length - 1}
              />
            </div>
          )}
          {!showNavButtons && (
            <button
              type="button"
              onClick={scrollToNext}
              className={clsx(
                styles.nextButton,
                styles.edgeButton,
                activeIndex === data.length - 1 && styles.disabled,
              )}
            />
          )}
          <Pagination length={data.length} activeIndex={activeIndex} onClick={scrollToIndex} />
        </>
      )}
    </View>
  );
}
